import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  flexRender
} from "@tanstack/react-table";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/ConvocazioneForm.css";

const API_URL = process.env.REACT_APP_API_URL;

const ConvocazioneForm = () => {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [eventi, setEventi] = useState([]);
  const [categorieGara, setCategorieGara] = useState([]);
  const [categorieGiocatoriDisponibili, setCategorieGiocatoriDisponibili] = useState([]);
  const [giocatori, setGiocatori] = useState([]);

  const [selectedEventoId, setSelectedEventoId] = useState("");
  const [selectedCategoriaGara, setSelectedCategoriaGara] = useState("");
  const [categorieGiocatori, setCategorieGiocatori] = useState([]);
  const [categoriaDaAggiungere, setCategoriaDaAggiungere] = useState("");

  const [garaSelezionata, setGaraSelezionata] = useState(null);
  const [oraGara, setOraGara] = useState("");
  const [indirizzoGara, setIndirizzoGara] = useState("");
  const [oraAppuntamento, setOraAppuntamento] = useState("");
  const [note, setNote] = useState("");

  const [rowSelection, setRowSelection] = useState({});
  const [convocazioneSalvata, setConvocazioneSalvata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingConvocazione, setLoadingConvocazione] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const getEventoId = (ev) => String(ev?.idEvento ?? ev?.id ?? "");
  const getEventoLabel = (ev) => ev?.nomeEvento ?? ev?.nome ?? "Evento";

  const getCategoriaId = (cat) => String(cat?.id ?? cat?.idCategoria ?? "");
  const getCategoriaLabel = (cat) => cat?.nome ?? cat?.categoria ?? "";

  const getGaraId = (gara) => gara?.idGara ?? gara?.id ?? null;
  const getGaraCategoriaId = (gara) =>
    String(gara?.idCategoria ?? gara?.categoria?.id ?? "");
  const getGaraOra = (gara) =>
    String(gara?.oraGara ?? gara?.oraInizio ?? "").substring(0, 5);
  const getGaraIndirizzo = (gara) =>
    gara?.indirizzo ?? gara?.campo ?? gara?.luogo ?? "";

  const getGiocatoreId = (g) => String(g?.idGiocatore ?? g?.id ?? "");
  const getGiocatoreCategoriaId = (g) =>
    String(g?.idCategoria ?? g?.categoria?.id ?? "");

  const parseErrorMessage = async (res, fallbackMessage) => {
    try {
      const contentType = res.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        const data = await res.json();
        return data?.message || data?.error || data?.details || fallbackMessage;
      }

      const text = await res.text();
      return text || fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  };

  const normalizeGiocatore = (g) => ({
    id: String(g?.idGiocatore ?? g?.id ?? ""),
    idGiocatore: String(g?.idGiocatore ?? g?.id ?? ""),
    nome: g?.nome ?? "",
    cognome: g?.cognome ?? "",
    idCategoria: String(g?.idCategoria ?? g?.categoria?.id ?? ""),
    nomeCategoria: g?.nomeCategoria ?? g?.categoria?.nome ?? ""
  });

  const resetConvocazioneState = useCallback(() => {
    setOraAppuntamento("");
    setNote("");
    setRowSelection({});
    setConvocazioneSalvata(null);
    setIsEditMode(false);
  }, []);

  const resetDettagliGara = useCallback(() => {
    setGaraSelezionata(null);
    setOraGara("");
    setIndirizzoGara("");
    setCategorieGiocatori([]);
    setCategoriaDaAggiungere("");
    setGiocatori([]);
    resetConvocazioneState();
  }, [resetConvocazioneState]);

  useEffect(() => {
    const fetchCategorieGara = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/allenamenti/mie-categorie`);

        if (!res.ok) {
          throw new Error(await parseErrorMessage(res, "Errore caricamento categorie gara"));
        }

        const data = await res.json();
        const lista = Array.isArray(data) ? data : [];

        setCategorieGara(lista);

        if (lista.length > 0) {
          setSelectedCategoriaGara((prev) => {
            if (prev && lista.some((cat) => getCategoriaId(cat) === String(prev))) {
              return prev;
            }
            return getCategoriaId(lista[0]);
          });
        } else {
          setSelectedCategoriaGara("");
        }
      } catch (err) {
        console.error("Errore fetch categorie gara:", err);
        toast.error(err.message || "Errore nel caricamento delle categorie gara.");
        setCategorieGara([]);
        setSelectedCategoriaGara("");
      }
    };

    fetchCategorieGara();
  }, [authFetch]);

  useEffect(() => {
    const fetchCategorieGiocatori = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/admin/categorie`);

        if (!res.ok) {
          throw new Error(await parseErrorMessage(res, "Errore caricamento categorie giocatori"));
        }

        const data = await res.json();
        const lista = Array.isArray(data) ? data : [];

        setCategorieGiocatoriDisponibili(lista);
      } catch (err) {
        console.error("Errore fetch categorie giocatori:", err);
        toast.error(err.message || "Errore nel caricamento delle categorie giocatori.");
        setCategorieGiocatoriDisponibili([]);
      }
    };

    fetchCategorieGiocatori();
  }, [authFetch]);

  useEffect(() => {
    const fetchEventi = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/eventi-gara/miei`);

        if (!res.ok) {
          throw new Error(await parseErrorMessage(res, "Errore caricamento eventi gara"));
        }

        const data = await res.json();
        setEventi(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Errore fetch eventi gara:", err);
        toast.error(err.message || "Errore nel caricamento degli eventi gara.");
        setEventi([]);
      }
    };

    fetchEventi();
  }, [authFetch]);

  useEffect(() => {
    const fetchGareEvento = async () => {
      if (!selectedEventoId || !selectedCategoriaGara) {
        resetDettagliGara();
        return;
      }

      try {
        const res = await authFetch(
          `${API_URL}/api/gare/mie/evento-gara/${selectedEventoId}`
        );

        if (!res.ok) {
          throw new Error(await parseErrorMessage(res, "Errore caricamento gare evento"));
        }

        const data = await res.json();
        const gare = Array.isArray(data) ? data : [];

        const gara = gare.find(
          (g) => getGaraCategoriaId(g) === String(selectedCategoriaGara)
        );

        if (!gara) {
          resetDettagliGara();
          return;
        }

        setGaraSelezionata(gara);
        setOraGara(getGaraOra(gara));
        setIndirizzoGara(getGaraIndirizzo(gara));
        resetConvocazioneState();

        const idCategoriaGara = getGaraCategoriaId(gara);
        if (idCategoriaGara) {
          setCategorieGiocatori([String(idCategoriaGara)]);
          setCategoriaDaAggiungere("");
        } else {
          setCategorieGiocatori([]);
          setCategoriaDaAggiungere("");
        }
      } catch (err) {
        console.error("Errore fetch gare evento:", err);
        toast.error(err.message || "Errore nel caricamento della gara.");
        resetDettagliGara();
      }
    };

    fetchGareEvento();
  }, [
    selectedEventoId,
    selectedCategoriaGara,
    authFetch,
    resetDettagliGara,
    resetConvocazioneState
  ]);

  useEffect(() => {
    const fetchConvocazioneEsistente = async () => {
      const idGara = getGaraId(garaSelezionata);

      if (!idGara) {
        setIsEditMode(false);
        setConvocazioneSalvata(null);
        setOraAppuntamento("");
        setNote("");
        setGiocatori([]);
        setRowSelection({});
        return;
      }

      try {
        setLoadingConvocazione(true);

        const res = await authFetch(`${API_URL}/api/convocazioni/gara/${idGara}`);

        if (res.status === 404) {
          setIsEditMode(false);
          setConvocazioneSalvata(null);
          setOraAppuntamento("");
          setNote("");
          setRowSelection({});
          return;
        }

        if (!res.ok) {
          throw new Error(await parseErrorMessage(res, "Errore caricamento convocazione"));
        }

        const data = await res.json();
        const convocati = Array.isArray(data?.convocati) ? data.convocati : [];

        setConvocazioneSalvata(data);
        setIsEditMode(true);
        setOraAppuntamento(data?.oraAppuntamento ?? "");
        setNote(data?.note ?? "");

        const giocatoriDaConvocazione = convocati.map((c) => ({
          id: String(c?.idGiocatore ?? ""),
          idGiocatore: String(c?.idGiocatore ?? ""),
          nome: c?.nome ?? "",
          cognome: c?.cognome ?? "",
          idCategoria: String(c?.idCategoriaGiocatore ?? ""),
          nomeCategoria: c?.nomeCategoriaGiocatore ?? ""
        }));

        setGiocatori(giocatoriDaConvocazione);

        const nuovaSelezione = {};
        giocatoriDaConvocazione.forEach((g) => {
          const id = String(g.idGiocatore || g.id || "");
          if (id) {
            nuovaSelezione[id] = true;
          }
        });
        setRowSelection(nuovaSelezione);
      } catch (err) {
        console.error("Errore fetch convocazione esistente:", err);
        toast.error(err.message || "Errore nel caricamento della convocazione.");
        setIsEditMode(false);
        setConvocazioneSalvata(null);
        setOraAppuntamento("");
        setNote("");
        setGiocatori([]);
        setRowSelection({});
      } finally {
        setLoadingConvocazione(false);
      }
    };

    fetchConvocazioneEsistente();
  }, [garaSelezionata, authFetch]);

  useEffect(() => {
    const fetchGiocatori = async () => {
      if (categorieGiocatori.length === 0) {
        if (!isEditMode) {
          setGiocatori([]);
          setRowSelection({});
        }
        return;
      }

      try {
        const risultati = await Promise.all(
          categorieGiocatori.map(async (idCategoria) => {
            const res = await authFetch(
              `${API_URL}/api/admin/giocatori?idCategoria=${encodeURIComponent(
                idCategoria
              )}&stato=ATTIVO&page=0&size=300`
            );

            if (!res.ok) {
              throw new Error(
                await parseErrorMessage(
                  res,
                  `Errore caricamento giocatori categoria ${idCategoria}`
                )
              );
            }

            const data = await res.json();

            if (Array.isArray(data)) return data.map(normalizeGiocatore);
            if (Array.isArray(data?.content)) return data.content.map(normalizeGiocatore);
            return [];
          })
        );

        const listaNuova = risultati.flat();

        setGiocatori((prev) => {
          const base = isEditMode ? prev : [];
          const merged = [...base, ...listaNuova];

          return merged.filter((g, index, arr) => {
            const id = String(g?.idGiocatore ?? g?.id ?? "");
            return index === arr.findIndex((x) => {
              const otherId = String(x?.idGiocatore ?? x?.id ?? "");
              return otherId === id;
            });
          });
        });

        if (!isEditMode) {
          setRowSelection({});
        }
      } catch (err) {
        console.error("Errore fetch giocatori:", err);
        toast.error(err.message || "Errore nel caricamento dei giocatori.");

        if (!isEditMode) {
          setGiocatori([]);
          setRowSelection({});
        }
      }
    };

    fetchGiocatori();
  }, [categorieGiocatori, authFetch, isEditMode]);

  const aggiungiCategoria = () => {
    if (!categoriaDaAggiungere) return;

    if (!categorieGiocatori.includes(categoriaDaAggiungere)) {
      setCategorieGiocatori((prev) => [...prev, categoriaDaAggiungere]);
    }

    setCategoriaDaAggiungere("");
  };

  const rimuoviCategoria = (idCategoria) => {
    setCategorieGiocatori((prev) => prev.filter((id) => id !== idCategoria));

    setRowSelection((prev) => {
      const updated = { ...prev };

      giocatori.forEach((g) => {
        if (getGiocatoreCategoriaId(g) === String(idCategoria)) {
          delete updated[getGiocatoreId(g)];
        }
      });

      return updated;
    });
  };

  const columns = useMemo(
    () => [
      {
        id: "seleziona",
        header: () => (
          <input
            type="checkbox"
            checked={
              giocatori.length > 0 &&
              Object.keys(rowSelection).length === giocatori.length
            }
            onChange={(e) => {
              const checked = e.target.checked;

              if (checked) {
                const nuovaSelezione = {};
                giocatori.forEach((g) => {
                  nuovaSelezione[getGiocatoreId(g)] = true;
                });
                setRowSelection(nuovaSelezione);
              } else {
                setRowSelection({});
              }
            }}
            ref={(input) => {
              if (!input) return;
              input.indeterminate =
                Object.keys(rowSelection).length > 0 &&
                Object.keys(rowSelection).length < giocatori.length;
            }}
          />
        ),
        cell: ({ row }) => {
          const id = getGiocatoreId(row.original);
          return (
            <input
              type="checkbox"
              checked={rowSelection[id] === true}
              onChange={() => {
                setRowSelection((old) => {
                  const updated = { ...old };
                  if (updated[id]) {
                    delete updated[id];
                  } else {
                    updated[id] = true;
                  }
                  return updated;
                });
              }}
            />
          );
        }
      },
      {
        id: "cognome",
        accessorFn: (row) => row?.cognome ?? "",
        header: "Cognome"
      },
      {
        id: "nome",
        accessorFn: (row) => row?.nome ?? "",
        header: "Nome"
      },
      {
        id: "categoria",
        accessorFn: (row) =>
          row?.nomeCategoria ||
          (() => {
            const idCategoria = String(row?.idCategoria ?? "");
            const cat = categorieGiocatoriDisponibili.find(
              (c) => String(getCategoriaId(c)) === idCategoria
            );
            return cat ? getCategoriaLabel(cat) : "";
          })(),
        header: "Categoria"
      }
    ],
    [giocatori, rowSelection, categorieGiocatoriDisponibili]
  );

  const table = useReactTable({
    data: giocatori,
    columns,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true
  });

  const buildPayload = () => {
    const idsSelezionati = Object.keys(rowSelection).filter(
      (id) => rowSelection[id] === true
    );

    return {
      idGara: getGaraId(garaSelezionata),
      oraAppuntamento,
      note: note?.trim() || null,
      convocati: idsSelezionati.map((idGiocatore) => ({
        idGiocatore: Number(idGiocatore),
        titolare: false,
        presente: true,
        note: ""
      }))
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEventoId) {
      toast.warning("Seleziona un evento gara.");
      return;
    }

    if (!selectedCategoriaGara) {
      toast.warning("Seleziona la categoria gara.");
      return;
    }

    if (!garaSelezionata) {
      toast.warning("Nessuna gara trovata per evento e categoria selezionati.");
      return;
    }

    if (!oraAppuntamento) {
      toast.warning("Inserisci l'ora appuntamento.");
      return;
    }

    const idsSelezionati = Object.keys(rowSelection).filter(
      (id) => rowSelection[id] === true
    );

    if (idsSelezionati.length === 0) {
      toast.warning("Seleziona almeno un giocatore.");
      return;
    }

    const payload = buildPayload();

    try {
      setLoading(true);

      let url = `${API_URL}/api/convocazioni`;
      let method = "POST";

      if (isEditMode && convocazioneSalvata?.id) {
        url = `${API_URL}/api/convocazioni/${convocazioneSalvata.id}`;
        method = "PUT";
      }

      const res = await authFetch(url, {
        method,
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(
          await parseErrorMessage(
            res,
            isEditMode
              ? "Errore nell'aggiornamento della convocazione."
              : "Errore nella creazione della convocazione."
          )
        );
      }

      const data = await res.json();
      setConvocazioneSalvata(data);
      setIsEditMode(true);

      toast.success(
        isEditMode
          ? "Convocazione aggiornata con successo."
          : "Convocazione creata con successo."
      );
    } catch (err) {
      console.error("Errore salvataggio convocazione:", err);
      toast.error(err.message || "Errore di rete o server.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!convocazioneSalvata?.id) {
      toast.warning("Devi prima salvare la convocazione.");
      return;
    }

    try {
      const res = await authFetch(
        `${API_URL}/api/convocazioni/${convocazioneSalvata.id}/pdf`
      );

      if (!res.ok) {
        throw new Error(await parseErrorMessage(res, "Errore nel download del PDF."));
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `convocazione_${convocazioneSalvata.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Errore download PDF:", err);
      toast.error(err.message || "Errore durante il download del PDF.");
    }
  };

  const eventoLabelSelezionato =
    eventi.find((ev) => getEventoId(ev) === String(selectedEventoId));

  const categoriaGaraLabelSelezionata =
    categorieGara.find((cat) => getCategoriaId(cat) === String(selectedCategoriaGara));

  return (
    <div className="convocazione-container" translate="no">
      <h2>{isEditMode ? "Modifica Convocazione" : "Nuova Convocazione"}</h2>

      <form onSubmit={handleSubmit} className="convocazione-form">
        <label>Evento Gara:</label>
        <select
          value={selectedEventoId}
          onChange={(e) => {
            setSelectedEventoId(e.target.value);
            resetDettagliGara();
          }}
          required
        >
          <option value="">-- Seleziona Evento --</option>
          {eventi.map((ev) => {
            const id = getEventoId(ev);
            return (
              <option key={`evento-${id}`} value={id}>
                {getEventoLabel(ev)}
              </option>
            );
          })}
        </select>

        <label>Categoria Gara:</label>
        <select
          value={selectedCategoriaGara}
          onChange={(e) => {
            setSelectedCategoriaGara(e.target.value);
            resetConvocazioneState();
          }}
          required
        >
          <option value="">-- Seleziona Categoria --</option>
          {categorieGara.map((cat) => {
            const id = getCategoriaId(cat);
            return (
              <option key={`cat-gara-${id}`} value={id}>
                {getCategoriaLabel(cat)}
              </option>
            );
          })}
        </select>

        <div className="convocazione-info-gara-card">
          <h3 className="convocazione-info-gara-title">Informazioni gara</h3>

          <div className="convocazione-info-gara-grid">
            <div className="convocazione-info-gara-item">
              <span className="convocazione-info-gara-label">Evento</span>
              <span className="convocazione-info-gara-value">
                {eventoLabelSelezionato ? getEventoLabel(eventoLabelSelezionato) : "-"}
              </span>
            </div>

            <div className="convocazione-info-gara-item">
              <span className="convocazione-info-gara-label">Categoria</span>
              <span className="convocazione-info-gara-value">
                {categoriaGaraLabelSelezionata
                  ? getCategoriaLabel(categoriaGaraLabelSelezionata)
                  : "-"}
              </span>
            </div>

            <div className="convocazione-info-gara-item">
              <span className="convocazione-info-gara-label">Inizio Gara</span>
              <span className="convocazione-info-gara-value">{oraGara || "-"}</span>
            </div>

            <div className="convocazione-info-gara-item convocazione-info-gara-item--full">
              <span className="convocazione-info-gara-label">Indirizzo</span>
              <span className="convocazione-info-gara-value">{indirizzoGara || "-"}</span>
            </div>
          </div>
        </div>

        <label>Ora Appuntamento:</label>
        <input
          type="time"
          value={oraAppuntamento}
          onChange={(e) => setOraAppuntamento(e.target.value)}
          required
          disabled={loadingConvocazione}
        />

        <label>Note:</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
          disabled={loadingConvocazione}
        />

        {isEditMode && (
          <div className="convocazione-esistente-info">
            Convocazione esistente caricata. Puoi modificare direttamente l’elenco dei convocati.
          </div>
        )}

        <>
          <label>Seleziona le categorie da cui scegliere i giocatori</label>
          <div className="convocazione-categorie-row">
            <select
              value={categoriaDaAggiungere}
              onChange={(e) => setCategoriaDaAggiungere(e.target.value)}
              disabled={loadingConvocazione}
            >
              <option value="">-- Seleziona Categoria --</option>
              {categorieGiocatoriDisponibili
                .filter((cat) => !categorieGiocatori.includes(getCategoriaId(cat)))
                .map((cat) => {
                  const id = getCategoriaId(cat);
                  return (
                    <option key={`cat-gioc-${id}`} value={id}>
                      {getCategoriaLabel(cat)}
                    </option>
                  );
                })}
            </select>

            <button type="button" onClick={aggiungiCategoria} disabled={loadingConvocazione}>
              +
            </button>
          </div>

          {categorieGiocatori.length > 0 && (
            <div className="convocazione-cat">
              {categorieGiocatori.map((idCat) => {
                const categoria = categorieGiocatoriDisponibili.find(
                  (cat) => getCategoriaId(cat) === String(idCat)
                );

                return (
                  <span key={`badge-${idCat}`} className="convocazione-badge">
                    {categoria ? getCategoriaLabel(categoria) : idCat}
                    <button type="button" onClick={() => rimuoviCategoria(idCat)}>
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </>

        <h3>Seleziona Giocatori:</h3>

        <div className="convocazione-tabella-container">
          <table className="convocazione-tabella">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={4}>Nessun giocatore disponibile.</td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={`row-${getGiocatoreId(row.original)}`}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="convocazione-button-row">
          <button
            type="submit"
            className="convocazione-btn-salva"
            disabled={loading || loadingConvocazione}
          >
            {loading
              ? (isEditMode ? "Aggiornamento..." : "Salvataggio...")
              : (isEditMode ? "Aggiorna Convocazione" : "Salva Convocazione")}
          </button>

          <button
            type="button"
            className="convocazione-btn-pdf"
            onClick={handleDownloadPdf}
            disabled={!convocazioneSalvata?.id}
          >
            Scarica PDF
          </button>

          <button
            type="button"
            className="convocazione-btn-torna"
            onClick={() => navigate("/gestionale")}
          >
            Torna al Gestionale
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConvocazioneForm;